import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { useParams } from 'react-router'
import { Box, InputRadio, Typography } from 'shared/ui'
import { AlertCalendlyFreePlan } from 'entities/Integrations'
import { AgentFooterButtons } from 'pages/chatbot/ui'
import { useChatbotContext } from 'pages/chatbot/context'
import { useBookAppointmentBlocker } from 'pages/chatbot/pages/agentBookAppointment/hooks/useBookAppointmentBlocker'
import { SmartMeeting } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/ui/SmartMeeting'
import { LinkTriggerContent } from './LinkTriggerContent'
import { SelectValueDropdown } from './SelectValueDropdown'
import { LinkDropdownCustomComponent } from './LinkDropdownCustomComponent'
import styles from './styles.module.scss'

export const ConnectCalendar = observer(() => {
  const chatbotStore = useChatbotContext()
  const { agentId: id } = useParams()
  const {
    agentBookAppointmentStepsStore: {
      connectCalendarStore,
      isExistChanges,
      confirmLeavePage,
      decreaseStep,
      agentActivateStore: {
        handleActivateClickWithModal,
        isFromContactOwner,
        isSendFromInitialized,
      },
    },
    model: { isActive, sendFrom },
  } = chatbotStore
  const {
    bookingTypes,
    bookingType,
    handleNextClick,
    setBookingType,
    isLoading,
    isCalendlyConnected,
    connectionList,
    setConnection,
    errorsMap,
    removeError,
    isMeetingLoading,
    meetingsList,
    search,
    setSearch,
    setLink,
    eventLink,
    setIsCalendlyConnected,
    handleSaveDraft,
    disabled,
    smartBookingEnabled,
    setSmartBookingEnabled,
  } = connectCalendarStore

  const connectionErrorMessage = errorsMap.get('connection')
  const eventLinkErrorMessage = errorsMap.get('book_appointment_link')
  const isContactOwner = isSendFromInitialized
    ? isFromContactOwner
    : sendFrom?.type === 'from_contact_owner'

  useEffect(() => {
    return () => {
      if (isCalendlyConnected) {
        setIsCalendlyConnected(false)
      }
    }
  }, [isCalendlyConnected])

  useBookAppointmentBlocker({
    isExistChanges,
    confirmLeavePage: (blocker) =>
      confirmLeavePage(
        blocker,
        id,
        isActive
          ? async (id) => {
              const result = await handleActivateClickWithModal(id, {
                isPublished: isActive,
                shouldRedirect: false,
              })
              return result
            }
          : handleSaveDraft
      ),
  })

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      height={'100%'}
      justifyContent={'space-between'}
      paddingLeft={'20px'}
      paddingRight={'20px'}
    >
      <div>
        <Box display={'flex'} flexDirection={'column'} marginTop={4} gap={1} marginBottom={-1.5}>
          <AlertCalendlyFreePlan />
        </Box>
        <Box display={'flex'} flexDirection={'column'} marginTop={4} gap={1} marginBottom={3}>
          <Typography
            variant={'body-sm-regular'}
            ariaLabel={'SchedulingTool_title'}
            color={'var(--content-primary-tertiary)'}
          >
            Scheduling tool
          </Typography>
          <SelectValueDropdown
            placeholder={'Select your booking page'}
            isValueSelected={isCalendlyConnected}
            isLoading={isLoading}
            itemsList={connectionList}
            setSelectedValue={setConnection}
            errorMessage={connectionErrorMessage}
            removeError={() => removeError('connection')}
            ariaLabel={'SelectConnection'}
          />
        </Box>
        {isCalendlyConnected && (
          <Box display={'flex'} flexDirection={'column'} marginTop={2} gap={1} marginBottom={3}>
            <Typography
              variant={'body-sm-regular'}
              ariaLabel={'ChooseLink_title'}
              color={'var(--content-primary-tertiary)'}
            >
              Choose an event link
            </Typography>
            <SelectValueDropdown
              placeholder={'Select event link'}
              isValueSelected={!!eventLink}
              isLoading={isMeetingLoading}
              errorMessage={eventLinkErrorMessage}
              ariaLabel={'SelectEventLink'}
              triggerContent={<LinkTriggerContent link={eventLink} />}
              customComponent={(onClose) => (
                <LinkDropdownCustomComponent
                  onClose={onClose}
                  search={search}
                  setSearch={setSearch}
                  itemsList={meetingsList}
                  setItem={setLink}
                  removeError={() => removeError('book_appointment_link')}
                />
              )}
            />
          </Box>
        )}
        <Typography
          variant={'body-lg-medium'}
          ariaLabel={'ConversationTitle'}
          color={'var(--content-primary-primary)'}
          marginBottom={4}
        >
          How should the AI Agent handle booking?
        </Typography>
        <Box display={'flex'} flexDirection={'column'} marginTop={2} marginBottom={3}>
          {bookingTypes.map((item) => (
            <InputRadio
              key={item.id}
              label={item.customLabel}
              checked={bookingType === item.id}
              onChecked={() => setBookingType(item.id)}
              className={classNames(styles.bookingRadio, {
                [styles.checked]: bookingType === item.id,
              })}
              radioSize='small'
            />
          ))}
        </Box>
        <SmartMeeting
          active={smartBookingEnabled}
          onChange={setSmartBookingEnabled}
          disabled={isContactOwner}
          switchTooltipProps={{
            label: "Not available when 'Send as contact owner' is selected.",
            visibility: isContactOwner ? 'visible' : 'hidden',
            placement: 'top',
            breakWord: true,
          }}
        />
      </div>

      <AgentFooterButtons
        decreaseStep={decreaseStep}
        handleNextClick={() => handleNextClick(id)}
        isNextButtonDisabled={disabled}
      />
    </Box>
  )
})
